import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { z } from 'zod';

import { Textarea } from '@heidi/design-system/textarea';

import { inviteTeamStepMessages } from './invite-team-step.messages';

const emailSchema = z.string().email();

/** Commas, semicolons and newlines all separate one address from the next. */
const SEPARATORS = /[,;\s]+/;

function parseEmails(text: string): string[] {
  return text.split(SEPARATORS).filter(Boolean);
}

interface InviteEmailTextareaProps {
  id: string;
  emails: string[];
  onChange: (emails: string[]) => void;
  /**
   * Whether everything typed so far parses. An empty field counts as valid —
   * the step's own buttons decide what an empty list means.
   */
  onValidChange: (isValid: boolean) => void;
  hasError: boolean;
}

/**
 * Free-text invite entry: one box the user pastes or types a list of addresses
 * into. The parent still owns a `string[]`, so the deferred-send path is
 * unchanged; this only moves where the splitting happens.
 */
export function InviteEmailTextarea({
  id,
  emails,
  onChange,
  onValidChange,
  hasError,
}: InviteEmailTextareaProps) {
  const intl = useIntl();
  const [text, setText] = useState(() => emails.join(', '));

  const parsed = parseEmails(text);
  const isValid = parsed.every((email) => emailSchema.safeParse(email).success);
  useEffect(() => {
    onValidChange(isValid);
  }, [isValid, onValidChange]);

  // The parent can hand us a list we did not type — it re-seeds saved invites
  // one render after mount (Back from a later step). Typing keeps the two in
  // step, so adopting anything that diverges never clobbers a keystroke.
  const parsedKey = parsed.join(',');
  const emailsKey = emails.join(',');
  useEffect(() => {
    if (parsedKey === emailsKey) return;
    setText(emails.join(', '));
  }, [emails, emailsKey, parsedKey]);

  return (
    <Textarea
      id={id}
      value={text}
      onChange={(event) => {
        setText(event.target.value);
        onChange(parseEmails(event.target.value));
      }}
      placeholder={intl.formatMessage(inviteTeamStepMessages.emailListPlaceholder)}
      // Fixed at the design's height; growth is bounded so a long paste
      // scrolls inside the box instead of pushing the footer off-screen.
      autoResize
      minRows={4}
      maxRows={8}
      inputMode="email"
      autoComplete="off"
      aria-invalid={hasError}
    />
  );
}
